// src/components/common/LegalPageLayout.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';

/**
 * Shared chrome for legal documents (Terms of Use, Privacy Policy, Legal Notice).
 * Each page passes bilingual content as { titleAr, titleFr, updatedAr, updatedFr, sections }
 * where `sections` is an array of { headingAr, headingFr, bodyAr, bodyFr } —
 * `body*` may be a string or an array of strings (rendered as separate paragraphs).
 */
const LegalPageLayout = ({ titleAr, titleFr, updatedAr, updatedFr, sections, intro }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const BackIcon = isAr ? ArrowForwardIcon : ArrowBackIcon;

  const renderBody = (body) => {
    const paragraphs = Array.isArray(body) ? body : [body];
    return paragraphs.map((p, idx) => (
      <Typography key={idx} variant="body1" sx={{ lineHeight: 1.9, mb: 1.5, color: 'text.secondary' }}>
        {p}
      </Typography>
    ));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 3, md: 6 }
      }}
    >
      <Container maxWidth="md">
        <Button
          onClick={() => navigate(-1)}
          startIcon={<BackIcon />}
          sx={{ color: 'white', mb: 2, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
        >
          {isAr ? 'رجوع' : 'Retour'}
        </Button>

        <Paper
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.97)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
              color: 'white',
              padding: { xs: 3, md: 4 },
              textAlign: 'center'
            }}
          >
            <GavelIcon sx={{ fontSize: 40, mb: 1.5 }} />
            <Typography variant="h5" fontWeight={700}>
              {isAr ? titleAr : titleFr}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 1 }}>
              {isAr ? updatedAr : updatedFr}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {intro && (
              <>
                <Box sx={{ mb: 2 }}>{renderBody(isAr ? intro.ar : intro.fr)}</Box>
                <Divider sx={{ mb: 3 }} />
              </>
            )}

            <Stack spacing={3.5} divider={<Divider flexItem />}>
              {sections.map((section, idx) => (
                <Box key={idx}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: '#2c3e50' }}>
                    {idx + 1}. {isAr ? section.headingAr : section.headingFr}
                  </Typography>
                  {renderBody(isAr ? section.bodyAr : section.bodyFr)}
                </Box>
              ))}
            </Stack>

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
              <Typography variant="caption" color="text.disabled" display="block">
                © {new Date().getFullYear()}{' '}
                {isAr
                  ? 'كلية العلوم القانونية والسياسية — سطات'
                  : 'Faculté des Sciences Juridiques et Politiques — Settat'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LegalPageLayout;
